import FullScreenLoader from "../../BON Frontend/Booking-Form/Common/FullScreenLoader";
import ReservationSidebar from '../Commun/ReservationSidebar';
import ReservationHeader from '../Commun/ReservationHeader';
import { useEffect, useState } from 'react';
import { Property, ReserBusinessSource } from '../../Services/Service';
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchBusinessPropertyYearlyReport, fetchBusinessYearlyReport } from '../../Redux/Slices/BusinessSourceReportSlice';
import * as XLSX from "xlsx";
import "./BusinessReportTheme.css";

const reportSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderColor: state.isFocused ? "#5b4cf6" : "#d7dff0",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(91, 76, 246, 0.12)" : "none",
        borderRadius: 8,
        fontSize: 12,
        "&:hover": { borderColor: "#5b4cf6" }
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: "#5146e8",
        borderRadius: 999,
        color: "#fff",
        paddingLeft: 4
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: "#fff",
        fontSize: 11,
        fontWeight: 600
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: "#fff",
        borderRadius: "0 999px 999px 0",
        ":hover": { backgroundColor: "#3b32c2", color: "#fff" }
    }),
    option: (base, state) => ({
        ...base,
        fontSize: 12,
        backgroundColor: state.isSelected ? "#5146e8" : state.isFocused ? "#f1f4ff" : "#fff",
        color: state.isSelected ? "#fff" : "#1d2540"
    }),
    placeholder: (base) => ({ ...base, color: "#7b849c", fontSize: 12 }),
    singleValue: (base) => ({ ...base, color: "#1d2540", fontSize: 12 })
};

export const BusinessYearlyReport = () => {
    const dispatch = useDispatch();

    const [propertyList, setPropertyList] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState([]);
    const [selectedYear, setSelectedYear] = useState([]);
    const [sources, setSources] = useState([]);
    const [selectedSource, setSelectedSource] = useState([]);
    const [reportType, setReportType] = useState("source");

    const loading = useSelector(state => state.businessSourceReport.loading);

    // 🔹 Fetch Sources
    useEffect(() => {
        const fetchBusinessSource = async () => {
            try {
                const data = await ReserBusinessSource(333333);
                if (Array.isArray(data)) setSources(data);
            } catch (error) {
                console.log(error);
            }
        };
        fetchBusinessSource();
    }, []);

    // 🔹 Default values
    useEffect(() => {
        fetchProperty();

        const now = new Date();

        setSelectedYear([{
            value: now.getFullYear(),
            label: now.getFullYear().toString()
        }]);
    }, []);

    const fetchProperty = async () => {
        const data = await Property();
        setPropertyList(data);

        setSelectedProperty(
            data.map(p => ({
                value: p.PropertyID,
                label: p.PropertyName
            }))
        );
    };

    // 🔹 Options
    const propertyOptions = propertyList.map(p => ({
        value: p.PropertyID,
        label: p.PropertyName
    }));

    const yearOptions = Array.from({ length: 10 }, (_, i) => {
        const y = new Date().getFullYear() - i;
        return { value: y, label: y.toString() };
    });

    const sourceOptions = sources
        ?.filter(s => s.BONGroupCode && s.BONBusinessSource)
        .map(s => ({
            value: s.BONGroupCode,
            label: s.BONBusinessSource
        }));

    // 🔹 Handlers
    const handlePropertyChange = v => setSelectedProperty(v || []);
    const handleYearChange = v => setSelectedYear(v || []);
    const handleSourceChange = v => setSelectedSource(v || []);

    // 🔹 Generate Yearly Table
    const generateYearlyPage = (doc, data, yearObj, today, isFirstPage, selectedProperty) => {

        const pageWidth = doc.internal.pageSize.getWidth();

        if (!isFirstPage) doc.addPage();

        const year = yearObj.value;

        // 🔹 Title
        doc.setFontSize(16);
        doc.text("Source Yearly Report", pageWidth / 2, 10, { align: "center" });

        doc.setFontSize(10);
        doc.text(`Generated: ${today}`, pageWidth - 10, 10, { align: "right" });

        // =========================
        // 🔹 PROPERTY BOX (SAME AS ABOVE)
        // =========================

        let propertyList = [];

        if (selectedProperty.length === propertyOptions.length) {
            propertyList = ["All Property"];
        } else {
            propertyList = selectedProperty.map(p => p.label);
        }

        const startX = 12;
        const boxY = 14;

        const label = "Property:";
        const labelWidth = doc.getTextWidth(label);

        const textStartX = startX + labelWidth + 6;

        const lineHeight = 5;

        const rowsProp = [];
        let currentLine = "";
        let currentWidth = 0;

        const maxWidth = pageWidth - textStartX - 12;

        propertyList.forEach((name, index) => {

            const isLast = index === propertyList.length - 1;

            const separator = isLast ? "" : "  |  ";
            const text = name + separator;

            const textWidth = doc.getTextWidth(text);

            if (currentWidth + textWidth <= maxWidth) {
                currentLine += text;
                currentWidth += textWidth;
            } else {
                rowsProp.push(currentLine.trim());
                currentLine = text;
                currentWidth = textWidth;
            }
        });

        if (currentLine) {
            rowsProp.push(currentLine.trim());
        }

        const boxHeight = (rowsProp.length * lineHeight) + 1.5;

        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(0.5);
        doc.rect(10, boxY, pageWidth - 20, boxHeight);

        doc.setFont(undefined, "bold");
        doc.setFontSize(10);
        doc.text("Property:", startX, boxY + 4);

        doc.setFont(undefined, "normal");

        let y = boxY + 4;

        rowsProp.forEach(line => {
            doc.text(line, textStartX, y);
            y += lineHeight;
        });

        const startY = y + 3;

        // =========================
        // 🔢 NUMBER FORMAT
        // =========================
        const formatNumber = (num) => {
            if (!num) return "0";
            return Number(num).toLocaleString("en-IN");
        };

        const months = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];

        // =========================
        // ✅ HEADER
        // =========================
        const headerRow = [
            "Business Source",
            "Type",
            ...months.map(m => `${m} ${year}`)
        ];

        // =========================
        // ✅ ROWS
        // =========================
        const rows = [];

        data
            .filter(item => {

                return months.some(m =>
                    Number(item[`${m}_OCC`] || 0) !== 0 ||
                    Number(item[`${m}_REV`] || 0) !== 0 ||
                    Number(item[`${m}_ARR`] || 0) !== 0
                );

            })
            .forEach(item => {

                rows.push([
                    {
                        content: item.BusinessSource || "",
                        rowSpan: 3,
                        styles: {
                            valign: 'middle',
                            halign: 'left'
                        }
                    },
                    {
                        content: "RM NTS",
                        styles: { halign: 'left' }
                    },
                    ...months.map(m => formatNumber(item[`${m}_OCC`] || 0))
                ]);

                rows.push([
                    {
                        content: "REV.",
                        styles: { halign: 'left' }
                    },
                    ...months.map(m => formatNumber(item[`${m}_REV`] || 0))
                ]);

                rows.push([
                    {
                        content: "ARR",
                        styles: { halign: 'left' }
                    },
                    ...months.map(m => formatNumber(item[`${m}_ARR`] || 0))
                ]);
            });

        // =========================
        // 🔹 WIDTH
        // =========================
        const totalCols = 2 + months.length;
        const totalWidth = pageWidth - 20;

        const firstColWidth = 35;
        const secondColWidth = 15;
        const otherColWidth = (totalWidth - firstColWidth - secondColWidth) / months.length;

        const columnStyles = {
            0: { cellWidth: firstColWidth, halign: 'left', valign: 'middle' },
            1: { cellWidth: secondColWidth, halign: 'center' }
        };

        for (let i = 2; i < totalCols; i++) {
            columnStyles[i] = { cellWidth: otherColWidth, halign: 'right' };
        }

        // =========================
        // 🔥 TABLE
        // =========================
        autoTable(doc, {
            startY,
            head: [headerRow],
            body: rows,
            theme: 'grid',

            styles: {
                fontSize: 7,
                cellPadding: 1.5,
                halign: 'center',
                valign: 'middle',
                lineWidth: 0.2
            },

            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255
            },

            columnStyles,
            margin: { left: 10, right: 10 }
        });
    };

    const generateYearlyPageProperty = (
        doc,
        data,
        yearObj,
        today,
        isFirstPage,
        SourceNames
    ) => {
        const pageWidth = doc.internal.pageSize.getWidth();

        if (!isFirstPage) {
            doc.addPage();
        }

        const year = yearObj.value;

        // 🔹 Title
        doc.setFontSize(16);
        doc.text("Property Source Yearly Report", pageWidth / 2, 10, { align: "center" });

        doc.setFontSize(10);
        doc.text(`Generated: ${today}`, pageWidth - 10, 10, { align: "right" });

        // ✅ SOURCE NAME SHOW
        if (SourceNames && SourceNames.length > 0) {
            doc.text(`Source: ${SourceNames.join(", ")}`, 10, 16);
        }

        const startY = SourceNames?.length ? 22 : 20;

        const months = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];

        // ✅ FORMAT FUNCTION
        const formatNumber = (num) => {
            if (!num) return "0";
            return Number(num).toLocaleString("en-IN");
        };

        // 🔹 HEADER
        const headerRow = [
            "Property Name",
            "Type",
            ...months.map(m => `${m} ${year}`)
        ];

        // 🔹 ROWS (🔥 MAIN CHANGE)
        const rows = [];

        data
            .filter(item => {

                return months.some(m =>
                    Number(item[`${m}_OCC`] || 0) !== 0 ||
                    Number(item[`${m}_REV`] || 0) !== 0 ||
                    Number(item[`${m}_ARR`] || 0) !== 0
                );

            })
            .forEach(item => {

                // 🔹 RM NTS ROW
                rows.push([
                    {
                        content: item.PropertyName || "",
                        rowSpan: 3,
                        styles: {
                            valign: 'middle',
                            halign: 'left'
                        }
                    },
                    {
                        content: "RM NTS",
                        styles: {
                            halign: 'left',
                            // light color
                        }
                    },
                    ...months.map(m => formatNumber(item[`${m}_OCC`] || 0))
                ]);

                // 🔹 REV ROW
                rows.push([
                    {
                        content: "REV.",
                        styles: {
                            halign: 'left',
                            // light color
                        }
                    },
                    ...months.map(m => formatNumber(item[`${m}_REV`] || 0))
                ]);

                // 🔹 REV ROW
                rows.push([
                    {
                        content: "ARR",
                        styles: {
                            halign: 'left',
                            // light color
                        }
                    },
                    ...months.map(m => formatNumber(item[`${m}_ARR`] || 0))
                ]);
            });

        // 🔥 WIDTH SAME LOGIC
        const totalCols = 2 + months.length;
        const totalWidth = pageWidth - 20;

        const firstColWidth = 35;
        const secondColWidth = 15;
        const otherColWidth = (totalWidth - firstColWidth - secondColWidth) / months.length;

        const columnStyles = {
            0: { cellWidth: firstColWidth, halign: 'left', valign: 'middle' },
            1: { cellWidth: secondColWidth, halign: 'center' }
        };

        for (let i = 2; i < totalCols; i++) {
            columnStyles[i] = { cellWidth: otherColWidth, halign: 'right' };
        }

        // 🔥 TABLE
        autoTable(doc, {
            startY,
            head: [headerRow],
            body: rows,
            theme: 'grid',

            styles: {
                fontSize: 7,
                cellPadding: 1.5,
                halign: 'center',
                valign: 'middle',
                lineWidth: 0.2
            },

            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255
            },

            columnStyles,
            margin: { left: 10, right: 10 }
        });
    };

    // 🔹 MAIN REPORT

    // const handleReport = async (e) => {
    //     e.preventDefault();

    //     if (!selectedProperty.length || !selectedYear.length) {
    //         alert("Please select required fields");
    //         return;
    //     }

    //     const doc = new jsPDF("landscape");
    //     const today = new Date().toLocaleDateString("en-GB", {
    //         day: "2-digit",
    //         month: "long",
    //         year: "numeric"
    //     });

    //     let isFirstPage = true;

    //     // 🔥 SOURCE REPORT
    //     if (reportType === "source") {

    //         const PID = selectedProperty.map(p => p.value);
    //         const Codes = selectedSource.map(s => s.value);

    //         for (const yearObj of selectedYear) {

    //             const payload = {
    //                 PID: `[${PID}]`,
    //                 Year: yearObj.value,
    //                 Code: Codes.length ? Codes[0] : ""
    //             };

    //             const result = await dispatch(fetchBusinessYearlyReport(payload));
    //             const data = result.payload || [];

    //             generateYearlyPage(
    //                 doc,
    //                 data,
    //                 yearObj,
    //                 today,
    //                 isFirstPage,
    //                 selectedProperty
    //             );

    //             isFirstPage = false;
    //         }

    //         doc.save("Source_Yearly_Report.pdf");
    //     }

    //     // 🔥 PROPERTY REPORT
    //     else {

    //         const PID = selectedProperty.map(p => p.value);
    //         const Codes = selectedSource.map(s => s.value);
    //         const SourceNames = selectedSource.map(s => s.label);

    //         for (const yearObj of selectedYear) {

    //             const payload = {
    //                 PID: `[${PID}]`,
    //                 Year: yearObj.value,
    //                 Code: Codes.length ? Codes[0] : ""
    //             };

    //             const result = await dispatch(fetchBusinessPropertyYearlyReport(payload));
    //             const data = result.payload || [];

    //             generateYearlyPageProperty(
    //                 doc,
    //                 data,
    //                 yearObj,
    //                 today,
    //                 isFirstPage,
    //                 SourceNames
    //             );

    //             isFirstPage = false;
    //         }

    //         doc.save("Property_Source_Yearly_Report.pdf");
    //     }
    // };

    const handleReport = async (e) => {
        e.preventDefault();

        if (!selectedProperty.length || !selectedYear.length) {
            alert("Please select required fields");
            return;
        }

        const doc = new jsPDF("landscape");

        const today = new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric"
        });

        let isFirstPage = true;

        const PID = selectedProperty.map(p => p.value);
        const Codes = selectedSource.map(s => s.value);
        const SourceNames = selectedSource.map(s => s.label);

        // =========================
        // 🔥 SOURCE REPORT
        // =========================
        if (reportType === "source") {

            for (const yearObj of selectedYear) {

                const payload = {
                    PID: `[${PID}]`,
                    Year: yearObj.value,

                    // ✅ FIX HERE (NO Codes[0])
                    Code: Codes.length ? `["${Codes.join('","')}"]` : ""
                };

                const result = await dispatch(fetchBusinessYearlyReport(payload));
                const data = result.payload || [];

                generateYearlyPage(
                    doc,
                    data,
                    yearObj,
                    today,
                    isFirstPage,
                    selectedProperty
                );

                isFirstPage = false;
            }

            doc.save("Source_Yearly_Report.pdf");
        }

        // =========================
        // 🔥 PROPERTY REPORT
        // =========================
        else {

            for (const yearObj of selectedYear) {

                const payload = {
                    PID: `[${PID}]`,
                    Year: yearObj.value,

                    // ✅ SAME FIX HERE
                    Code: Codes.length ? `["${Codes.join('","')}"]` : ""
                };

                const result = await dispatch(fetchBusinessPropertyYearlyReport(payload));
                const data = result.payload || [];

                generateYearlyPageProperty(
                    doc,
                    data,
                    yearObj,
                    today,
                    isFirstPage,
                    SourceNames
                );

                isFirstPage = false;
            }

            doc.save("Property_Source_Yearly_Report.pdf");
        }
    };

    const handleClear = () => {
        setSelectedProperty([]);
        setSelectedYear([]);
        setSelectedSource([]);
    };


    const buildYearlyExcelSheet = (data, year, nameKey, nameLabel) => {
        const months = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];

        const headerRow = [
            nameLabel,
            "Type",
            ...months.map(m => `${m} ${year}`)
        ];

        const filteredData = data.filter(item =>
            months.some(m =>
                Number(item[`${m}_OCC`] || 0) !== 0 ||
                Number(item[`${m}_REV`] || 0) !== 0 ||
                Number(item[`${m}_ARR`] || 0) !== 0
            )
        );

        const rows = [];
        const merges = [];

        filteredData.forEach((item, idx) => {
            // header row is row 0, so data starts at row 1
            const baseRow = 1 + (idx * 3);

            rows.push([
                item[nameKey] || "",
                "RM NTS",
                ...months.map(m => Number(item[`${m}_OCC`] || 0))
            ]);

            rows.push([
                "",
                "REV.",
                ...months.map(m => Number(item[`${m}_REV`] || 0))
            ]);

            rows.push([
                "",
                "ARR",
                ...months.map(m => Number(item[`${m}_ARR`] || 0))
            ]);

            // merge name column across the 3 rows for this item
            merges.push({
                s: { r: baseRow, c: 0 },
                e: { r: baseRow + 2, c: 0 }
            });
        });

        const aoa = [headerRow, ...rows];

        return { aoa, merges };
    };


    const handleExcelExport = async (e) => {
        e.preventDefault();

        if (!selectedProperty.length || !selectedYear.length) {
            alert("Please select required fields");
            return;
        }

        const PID = selectedProperty.map(p => p.value);
        const Codes = selectedSource.map(s => s.value);

        const wb = XLSX.utils.book_new();

        if (reportType === "source") {

            for (const yearObj of selectedYear) {

                const payload = {
                    PID: `[${PID}]`,
                    Year: yearObj.value,
                    Code: Codes.length ? `["${Codes.join('","')}"]` : ""
                };

                const result = await dispatch(fetchBusinessYearlyReport(payload));
                const data = result.payload || [];

                const { aoa, merges } = buildYearlyExcelSheet(
                    data, yearObj.value, "BusinessSource", "Business Source"
                );

                const ws = XLSX.utils.aoa_to_sheet(aoa);
                ws['!merges'] = merges;
                XLSX.utils.book_append_sheet(wb, ws, `Source_${yearObj.value}`);
            }

            XLSX.writeFile(wb, "Source_Yearly_Report.xlsx");

        } else {

            for (const yearObj of selectedYear) {

                const payload = {
                    PID: `[${PID}]`,
                    Year: yearObj.value,
                    Code: Codes.length ? `["${Codes.join('","')}"]` : ""
                };

                const result = await dispatch(fetchBusinessPropertyYearlyReport(payload));
                const data = result.payload || [];

                const { aoa, merges } = buildYearlyExcelSheet(
                    data, yearObj.value, "PropertyName", "Property Name"
                );

                const ws = XLSX.utils.aoa_to_sheet(aoa);
                ws['!merges'] = merges;
                XLSX.utils.book_append_sheet(wb, ws, `Property_${yearObj.value}`);
            }

            XLSX.writeFile(wb, "Property_Source_Yearly_Report.xlsx");
        }
    };

    return (
        <>
            {loading && (
                <FullScreenLoader />
            )}

            <div className="wrapper">
                <ReservationSidebar />
                <div className="main-panel">
                    <ReservationHeader />

                    <div className="container analytics-report">
                        <div className="page-inner analytics-report__inner">
                            <div className="analytics-report__header">
                                <div>
                                    <h3>
                                        {reportType === "source"
                                            ? "Source wise Yearly Report"
                                            : "Property Source wise Yearly Report"}
                                    </h3>
                                    <p>Analyze annual performance metrics across multiple property sources and locations.</p>
                                </div>
                                
                            </div>

                            <form className="analytics-report__filters">
                                <div className="analytics-report__field analytics-report__field--full">
                                    <label>Selected Properties</label>
                                    <Select
                                        options={propertyOptions}
                                        isMulti
                                        value={selectedProperty}
                                        onChange={handlePropertyChange}
                                        placeholder="Select properties..."
                                        classNamePrefix="analytics-select"
                                        styles={reportSelectStyles}
                                    />
                                </div>

                                <div className="analytics-report__grid analytics-report__grid--yearly">
                                    <div className="analytics-report__field">
                                        <label>Year</label>
                                        <Select
                                            options={yearOptions}
                                            isMulti
                                            value={selectedYear}
                                            onChange={handleYearChange}
                                            placeholder="Select year..."
                                            classNamePrefix="analytics-select"
                                            styles={reportSelectStyles}
                                        />
                                    </div>

                                    <div className="analytics-report__field">
                                        <label>Source</label>
                                        <Select
                                            options={sourceOptions}
                                            isMulti
                                            value={selectedSource}
                                            onChange={handleSourceChange}
                                            placeholder="Select source..."
                                            classNamePrefix="analytics-select"
                                            styles={reportSelectStyles}
                                        />
                                    </div>

                                    <div className="analytics-report__field">
                                        <label>Report Type</label>
                                        <div className="analytics-report__radios">
                                            <label>
                                                <input
                                                    type='radio'
                                                    name="reportType"
                                                    checked={reportType === "source"}
                                                    onChange={() => setReportType("source")}
                                                />
                                                Source
                                            </label>
                                            <label>
                                                <input
                                                    type='radio'
                                                    name="reportType"
                                                    checked={reportType === "property"}
                                                    onChange={() => setReportType("property")}
                                                />
                                                Property
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="analytics-report__actions">
                                    <button onClick={handleReport} className="analytics-report__btn analytics-report__btn--primary">
                                        <i className="fas fa-file-pdf"></i>
                                        Download PDF
                                    </button>

                                    <button className="analytics-report__btn analytics-report__btn--secondary" onClick={handleExcelExport}>
                                        <i className="fas fa-table"></i>
                                        Export Excel
                                    </button>

                                    <button type="button" className="analytics-report__btn analytics-report__btn--danger" onClick={handleClear}>
                                        <i className="fas fa-filter"></i>
                                        Clear Filter
                                    </button>
                                </div>
                            </form>

                            <div className="analytics-report__content">
                                <div className="analytics-report__empty">
                                    <div className="analytics-report__empty-icon">
                                        <i className="far fa-chart-bar"></i>
                                    </div>
                                    <h4>Ready to Generate</h4>
                                    <p>Adjust your filters and select the output format above to visualize the yearly distribution report.</p>
                                </div>

                                
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
